import { useCallback } from 'react';

import { ApolloError, useMutation } from '@apollo/client';
import * as Sentry from '@sentry/react';
import to from 'await-to-js';
import { v4 as uuidv4 } from 'uuid';

import type {
  BatchActionActionInput,
  BatchActionSelectionInput
} from '@/apollo/graphql';
import { notify } from '@/components/toast';
import { CREATE_BATCH_ACTION_MUTATION } from '@/gql/queries/batch-action.gql';

type UseCreateBatchActionOptions = {
  onSuccess?: (jobId: string) => void;
};

// A rejected selection (e.g. over the id ceiling) carries the limit in the
// server message, so a generic toast would hide the only actionable part.
// Network/unknown failures keep the generic copy.
const submitErrorMessage = (err: Error): string => {
  if (err instanceof ApolloError && err.graphQLErrors.length > 0) {
    return err.graphQLErrors[0].message;
  }
  return 'Something went wrong while starting the batch action...';
};

export type BatchActionArgs = {
  // actionTitle/workflowName are optional human labels persisted on the job for
  // the end-of-job digest email; they are part of BatchActionActionInput now.
  action: BatchActionActionInput;
  selection: BatchActionSelectionInput;
};

/**
 * Creates a server-side batch action job (STMN-3359). Replaces the per-trace
 * init + client-side signing + createLinks flow with a single `createBatchAction`
 * mutation. A fresh client-side `idempotencyKey` is generated per submit so a
 * double-click resolves to the same job (the backend dedupes on it).
 *
 * Returns the created `jobId` (poll it with `useBatchJob`), or `null` on error.
 */
export function useCreateBatchAction(options?: UseCreateBatchActionOptions) {
  const [createBatchActionMutation] = useMutation(CREATE_BATCH_ACTION_MUTATION);

  const createBatchAction = useCallback(
    async ({ action, selection }: BatchActionArgs): Promise<string | null> => {
      const idempotencyKey = uuidv4();

      // Map to the fields BatchActionActionInput defines. actionTitle /
      // workflowName are human labels persisted on the job for the digest email
      // (undefined when the caller doesn't provide them).
      const actionInput = {
        workflowId: action.workflowId,
        actionKey: action.actionKey,
        groupLabel: action.groupLabel,
        formData: action.formData,
        actionTitle: action.actionTitle ?? undefined,
        workflowName: action.workflowName ?? undefined
      };

      const promise = createBatchActionMutation({
        variables: {
          input: {
            idempotencyKey,
            action: actionInput,
            selection
          }
        }
      });

      // No success/loading toast: the batch progress toast (popped by
      // onSuccess) is the launch feedback. We only surface failures here.
      const [err, result] = await to(promise);
      if (err) {
        Sentry.captureException(err, {
          tags: { component: 'useCreateBatchAction' },
          extra: { action, selection }
        });
        notify.error(submitErrorMessage(err));
        return null;
      }

      const jobId = result?.data?.createBatchAction?.jobId ?? null;
      if (jobId) {
        options?.onSuccess?.(jobId);
      }
      return jobId;
    },
    [createBatchActionMutation, options?.onSuccess]
  );

  return createBatchAction;
}
